/**
 * @fileoverview StatutoryLiabilitiesChart - Stacked bar chart for statutory obligations
 *
 * Purpose: Display taxation, superannuation, and other statutory liabilities.
 * Features polished styling: rounded top bars, subtle grid, clean axes.
 *
 * Used by: Review phase statutory compliance analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  periodAlignedLayout,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  taxation: { label: "Taxation", color: "hsl(var(--chart-1))" },
  superannuation: { label: "Superannuation", color: "hsl(var(--chart-3))" },
  other: { label: "Other", color: "hsl(var(--chart-5))" },
} satisfies ChartConfig;

const dollarFormatter = (v: number) => `$${(v / 1000).toFixed(0)}k`;

export interface StatutoryLiabilitiesChartProps extends PeriodAlignedChartProps {
  periods: string[];
  taxation: (number | null)[];
  superannuation: (number | null)[];
  other: (number | null)[];
}

/** Stacked bars of statutory liabilities per period by category; nulls leave gaps. */
export function StatutoryLiabilitiesChart({
  periods,
  taxation,
  superannuation,
  other,
  plotInsetLeft,
}: StatutoryLiabilitiesChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 60);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    period,
    taxation: taxation[i] ?? null,
    superannuation: superannuation[i] ?? null,
    other: other[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <BarChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={dollarFormatter}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(v) =>
                typeof v === "number" ? dollarFormatter(v) : "—"
              }
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar dataKey="taxation" stackId="a" fill="hsl(var(--chart-1))" />
        <Bar dataKey="superannuation" stackId="a" fill="hsl(var(--chart-3))" />
        <Bar
          dataKey="other"
          stackId="a"
          fill="hsl(var(--chart-5))"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}
